import {
  ICustomer,
  ICustomerWalletDetails,
  ICustomerWalletDetailsResponse,
  ILoggedCustomer,
  ISignUpCustomerResponse,
  PhoneNumberAvailability,
  TabletOperationMode
} from "api/types";
import { ITenantLocation } from "api/types";
import ApolloClient from "apollo-boost";
import gql from "graphql-tag";
import Hashes from "jshashes";
import { store } from "utils/ConfigLoader";

export const getPhoneNumberAvailability = async (
  apollo: ApolloClient<{}>,
  phoneNumber: string
): Promise<PhoneNumberAvailability> => {
  const result: any = await apollo.query({
    query: gql`
      query getPhoneNumberAvailabilityAtSubjectLocation($phoneNumber: String) {
        getPhoneNumberAvailabilityAtSubjectLocation(phoneNumber: $phoneNumber)
      }
    `,
    variables: {
      phoneNumber
    },
    fetchPolicy: "network-only"
  });

  console.log(
    "Phone Number ",
    phoneNumber,
    " has availability: ",
    result.data.getPhoneNumberAvailabilityAtSubjectLocation
  );
  return result.data.getPhoneNumberAvailabilityAtSubjectLocation;
};

export const signInCustomer = async (
  apollo: ApolloClient<{}>,
  phoneNumber: string,
  pin: string,
  tabletOperationMode: TabletOperationMode,
  isMockSignIn: boolean
): Promise<ILoggedCustomer | undefined> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation signInCustomer(
        $phoneNumber: String!
        $pin: String
        $tabletOperationMode: TabletOperationMode
        $isMockSignIn: Boolean
      ) {
        signInCustomer(
          phoneNumber: $phoneNumber
          pin: $pin
          tabletOperationMode: $tabletOperationMode
          isMockSignIn: $isMockSignIn
        ) {
          authenticationToken
          pointsEarned
          customerDetails {
            phoneNumber
            pointsBalance
            interestIds
            usageTypeIds
          }
        }
      }
    `,
    variables: {
      phoneNumber,
      pin,
      tabletOperationMode,
      isMockSignIn
    }
  });

  return result.data.signInCustomer.authenticationToken
    ? {
        authToken: result.data.signInCustomer.authenticationToken,
        pointsEarned: result.data.signInCustomer.pointsEarned,
        newCustomer: false,
        newLocation: false, // TODO use the real newLocation returned value
        customer: result.data.signInCustomer.customerDetails,
        skipInterestsStep:
          result.data.signInCustomer.customerDetails.interestIds.length >= 0,
        hashedPin: pin ? new Hashes.MD5().hex(pin) : ""
      }
    : undefined;
};

export const setCustomerSession = (
  apollo: ApolloClient<{}>,
  loggedCustomer: ILoggedCustomer
) => {
  apollo.writeData({
    data: {
      loggedCustomer: {
        authToken: loggedCustomer.authToken,
        pointsEarned: loggedCustomer.pointsEarned,
        newCustomer: loggedCustomer.newCustomer,
        newLocation: loggedCustomer.newLocation,
        customer: loggedCustomer.customer,
        hashedPin: loggedCustomer.hashedPin,
        skipInterestsStep: loggedCustomer.skipInterestsStep,
        __typename: "LoggedCustomer"
      }
    }
  });
};

export const clearCustomerSession = (apollo: ApolloClient<{}>) => {
  store("purchase.promoCode", "");
  store("purchase.pointsEarned", 0);
  store("purchase.totalPrice", 0);
  store("purchase.categoryList", null);
  apollo.writeData({
    data: {
      loggedCustomer: null
    }
  });
};

export const signUpCustomer = async (
  apollo: ApolloClient<{}>,
  customer: ICustomer,
  tabletOperationMode: TabletOperationMode
): Promise<ILoggedCustomer | undefined> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation signUpCustomer(
        $customer: CustomerInput
        $tabletOperationMode: TabletOperationMode
      ) {
        signUpCustomer(
          customer: $customer
          tabletOperationMode: $tabletOperationMode
        ) {
          authenticationToken
          pointsEarned
          customerDetails {
            firstName
            lastName
            email
            # birthday
            phoneNumber
            pointsBalance
            interestIds
            usageTypeIds
          }
        }
      }
    `,
    variables: {
      customer,
      tabletOperationMode
    }
  });

  return transformSignedUpCustomer(result.data.signUpCustomer, customer);
};

export const transformSignedUpCustomer = (
  data: Pick<
    ISignUpCustomerResponse,
    "authenticationToken" | "pointsEarned" | "customerDetails"
  >,
  customer: Pick<ICustomer, "pin">
): ILoggedCustomer | undefined =>
  data.authenticationToken
    ? {
        authToken: data.authenticationToken,
        pointsEarned: data.pointsEarned,
        newCustomer: true,
        newLocation: false,
        hashedPin: customer.pin ? new Hashes.MD5().hex(customer.pin) : "",
        customer: data.customerDetails
      }
    : undefined;

export const generateAndSendNewPin = async (
  apollo: ApolloClient<{}>,
  phoneNumber: string
): Promise<void> => {
  await apollo.mutate({
    mutation: gql`
      mutation generateAndSendNewPin($phoneNumber: String!) {
        generateAndSendNewPin(phoneNumber: $phoneNumber)
      }
    `,
    variables: {
      phoneNumber
    }
  });
};

export const getPointsBalance = async (
  apollo: ApolloClient<{}>,
  authToken: string
): Promise<number> => {
  const result: any = await apollo.query({
    query: gql`
      query getPointsBalance($authToken: String!) {
        getPointsBalance(authToken: $authToken) {
          pointsBalance
        }
      }
    `,
    variables: {
      authToken
    },
    fetchPolicy: "network-only"
  });

  return result.data.getPointsBalance.pointsBalance;
};

// Blockchain
export const getCustomerWalletDetails = async (
  apollo: ApolloClient<{}>,
  customerId: string
): Promise<ICustomerWalletDetailsResponse> => {
  const result: any = await apollo.query({
    query: gql`
      query getCustomerWalletDetails($customerId: ID!) {
        getCustomerWalletDetails(customerId: $customerId) {
          walletStatus
          walletDetails {
            tenantId
            customerId
            assetSymbol
            assetName
            accountAddress
            accountAssetBalance
          }
        }
      }
    `,
    variables: { customerId },
    fetchPolicy: "network-only"
  });

  return result.data.getCustomerWalletDetails;
};

export const activateCustomerWallet = async (
  apollo: ApolloClient<{}>,
  customerId: string
): Promise<ICustomerWalletDetails> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation activateCustomerWallet($customerId: ID!) {
        activateCustomerWallet(customerId: $customerId) {
          tenantId
          customerId
          assetSymbol
          assetName
          accountAddress
          accountAssetBalance
        }
      }
    `,
    variables: { customerId },
    fetchPolicy: "no-cache"
  });
  return result.data.activateCustomerWallet;
};

export const sendCustomerWalletDetailsBySMS = async (
  apollo: ApolloClient<{}>,
  customerId: string
): Promise<void> => {
  await apollo.mutate({
    mutation: gql`
      mutation sendCustomerWalletDetailsBySMS($customerId: ID!) {
        sendCustomerWalletDetailsBySMS(customerId: $customerId)
      }
    `,
    variables: {
      customerId
    },
    fetchPolicy: "no-cache"
  });
};

export const fetchAvailableCustomerInterests = async (
  apollo: ApolloClient<{}>
): Promise<{
  interests: any;
  usageTypes: any;
}> => {
  const result: any = await apollo.query({
    query: gql`
      query availableSegmentationDetails {
        availableSegmentationDetails {
          interests {
            id
            name
            image
          }
          usageTypes {
            id
            name
          }
        }
      }
    `
  });

  return {
    interests: result.data.availableSegmentationDetails.interests,
    usageTypes: result.data.availableSegmentationDetails.usageTypes
  };
};

export const getCustomerDetails = async (
  apollo: ApolloClient<{}>,
  authToken: string
): Promise<any> => {
  const result: any = await apollo.query({
    query: gql`
      query getCustomerByAuthToken($authToken: String!) {
        getCustomerByAuthToken(authToken: $authToken) {
          phoneNumber
          pointsBalance
          interestIds
          usageTypeIds
        }
      }
    `,
    variables: {
      authToken
    },
    fetchPolicy: "network-only"
  });

  return result.data.getCustomerByAuthToken;
};

export const getCurrentLocation = async (
  apollo: ApolloClient<{}>
): Promise<ITenantLocation> => {
  const result: any = await apollo.query({
    query: gql`
      query currentLocation {
        me {
          ... on TenantTabletSelfInfo {
            location {
              name
              locationId
              tenantId
              vendorId
            }
          }
        }
      }
    `,
    fetchPolicy: "network-only"
  });

  return result.data.me.location;
};

export const updatePin = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  newPin: string
): Promise<boolean> => {
  const result = await apollo.mutate({
    mutation: gql`
      mutation updatePin($authToken: String!, $newPin: String!) {
        updatePin(authToken: $authToken, newPin: $newPin)
      }
    `,
    variables: {
      authToken,
      newPin
    }
  });

  if (result.data && result.data.updatePin) {
    apollo.writeData({
      data: {
        loggedCustomer: {
          hashedPin: newPin ? new Hashes.MD5().hex(newPin) : "",
          __typename: "LoggedCustomer"
        }
      }
    });
  }

  return result.data ? result.data.updatePin : false;
};

export const updateCustomerInterestsAndUsageTypes = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  newInterestIds: string[],
  newUsageTypeIds: string[]
): Promise<any> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation updateCustomerInterestsAndUsageTypes(
        $authToken: String!
        $newInterestIds: [String]!
        $newUsageTypeIds: [String]!
      ) {
        updateCustomerInterestsAndUsageTypes(
          authToken: $authToken
          newInterestIds: $newInterestIds
          newUsageTypeIds: $newUsageTypeIds
        )
      }
    `,
    variables: {
      authToken,
      newInterestIds,
      newUsageTypeIds
    }
  });

  return result.data.updateCustomerInterestsAndUsageTypes;
};

export const getRewards = async (apollo: ApolloClient<{}>): Promise<any> => {
  const result: any = await apollo.query({
    query: gql`
      query availableRewards {
        availableRewards {
          id
          name
          pointsRequired
          imageId
        }
      }
    `,
    fetchPolicy: "network-only"
  });

  return result.data.availableRewards;
};

export const redeemPointsForReward = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  tenantEmployeePin: string,
  rewardsAndQuantities: Array<{
    id: string;
    quantity: number;
  }>
): Promise<{
  pointsUsed: number;
  updatedCustomerPointBalance: number;
}> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation redeemPointsForReward(
        $authToken: String!
        $tenantEmployeePin: String!
        $rewardsAndQuantities: [RewardsAndQuantities]!
      ) {
        redeemPointsForReward(
          authToken: $authToken
          tenantEmployeePin: $tenantEmployeePin
          rewardsAndQuantities: $rewardsAndQuantities
        ) {
          pointsUsed
          updatedCustomerPointBalance
        }
      }
    `,
    variables: {
      authToken,
      tenantEmployeePin,
      rewardsAndQuantities
    }
  });

  return result.data.redeemPointsForReward;
};

export const recordPurchase = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  tenantEmployeePin: string,
  purchaseAmountDollars: number,
  purchaseDescriptionCategoryIds: string[],
  promoCode: string
): Promise<{
  pointsEarned: number;
}> => {
  const result: any = await apollo.mutate({
    mutation: gql`
      mutation recordPurchase(
        $authToken: String!
        $tenantEmployeePin: String!
        $purchaseAmountDollars: Int!
        $purchaseDescriptionCategoryIds: [ID!]!
        $promoCode: String
      ) {
        recordPurchase(
          authToken: $authToken
          tenantEmployeePin: $tenantEmployeePin
          purchaseAmountDollars: $purchaseAmountDollars
          purchaseDescriptionCategoryIds: $purchaseDescriptionCategoryIds
          promoCode: $promoCode
        ) {
          pointsEarned
        }
      }
    `,
    variables: {
      authToken,
      tenantEmployeePin,
      purchaseAmountDollars,
      purchaseDescriptionCategoryIds,
      promoCode
    }
  });

  return result.data.recordPurchase;
};

export const validateTenantUserPin = async (
  apollo: ApolloClient<{}>,
  pin: string
): Promise<{
  userId: string;
  invalidityCause: string;
}> => {
  const result: any = await apollo.query({
    query: gql`
      query validateTenantUserPin($pin: String!) {
        validateTenantUserPin(pin: $pin) {
          userId
          invalidityCause
        }
      }
    `,
    variables: {
      pin
    },
    fetchPolicy: "network-only"
  });

  return result.data.validateTenantUserPin;
};

export const validatePromoCode = async (
  apollo: ApolloClient<{}>,
  authToken: string,
  code: string
): Promise<any> => {
  const result: any = await apollo.query({
    query: gql`
      query validatePromoCodeForSubjectLocation(
        $authToken: String!
        $code: String!
      ) {
        validatePromoCodeForSubjectLocation(
          authToken: $authToken
          code: $code
        ) {
          name
          details
          status
        }
      }
    `,
    variables: {
      authToken,
      code
    },
    fetchPolicy: "network-only"
  });

  return result.data.validatePromoCodeForSubjectLocation;
};



// WEBPACK FOOTER //
// ./src/api/index.ts